import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiFetch } from '../../api/client';

const QUILL_EDITAL_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formEditalPadrao = (numero = 1, objetoTr = '') => {
  const agora = new Date();
  const em7 = new Date(agora.getTime() + 7 * 24 * 60 * 60 * 1000);
  const textoBase =
    typeof objetoTr === 'string' && objetoTr.trim()
      ? `<p><strong>Objeto:</strong></p><p>${objetoTr.replace(/\n/g, '<br/>')}</p>`
      : '';
  return {
    numero: Math.max(1, Number(numero) || 1),
    dataAbertura: toLocalInput(agora),
    dataEncerramento: toLocalInput(em7),
    prazoDias: 7,
    textoEdital: textoBase,
    status: 'RASCUNHO',
    modoPublico: true,
    dataAssinaturaPrevista: '',
  };
};

const diasAte = (iso) => {
  if (!iso) return null;
  const fim = new Date(iso);
  if (Number.isNaN(fim.getTime())) return null;
  return Math.ceil((fim.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

/**
 * Painel de configuração/reabertura de edital.
 * Persistência somente via API (sem localStorage).
 */
export default function PainelEditalContratacao({
  processoId,
  limiteReaberturas = 3,
  prazoAlertaDias = 3,
  disabled = false,
  showActions = true,
  confirmLabel = 'Salvar edital',
  onConfirm = null,
  onCancel = null,
  onSaved = null,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState(() => formEditalPadrao(1));
  const [historico, setHistorico] = useState([]);
  const [objetoTr, setObjetoTr] = useState('');
  const [portal, setPortal] = useState({
    publicado: false,
    token: null,
    numeroOficial: null,
    linkPath: null,
    codigo: null,
  });

  const limite = Math.max(1, Number(limiteReaberturas) || 3);
  const alertaDias = Math.max(1, Number(prazoAlertaDias) || 3);

  const carregar = useCallback(async () => {
    if (!processoId) return;
    setLoading(true);
    setErro('');
    try {
      const res = await apiFetch(`/api/contratos/processos/${processoId}/extensoes`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao carregar edital');
      const obj = data.objetoTr || '';
      setObjetoTr(obj);
      const atual = data.editalAtual;
      if (atual && typeof atual === 'object') {
        setForm({
          ...formEditalPadrao(atual.numero || 1, obj),
          ...atual,
          dataAbertura: atual.dataAbertura || formEditalPadrao().dataAbertura,
          dataEncerramento: atual.dataEncerramento || formEditalPadrao().dataEncerramento,
          modoPublico: atual.modoPublico !== false,
          dataAssinaturaPrevista:
            data.dataAssinaturaPrevista || atual.dataAssinaturaPrevista || '',
          textoEdital: atual.textoEdital || formEditalPadrao(1, obj).textoEdital,
        });
      } else {
        setForm(formEditalPadrao(1, obj));
      }
      setHistorico(Array.isArray(data.historicoEditais) ? data.historicoEditais : []);
      const token = data.editalToken || atual?.token || null;
      const linkPath =
        data.editalLinkPath ||
        atual?.linkPath ||
        (token ? `/portal-prestador/propostas/${token}` : null);
      setPortal({
        publicado: !!(data.isEditalPublico || token),
        token,
        numeroOficial: data.editalNumeroOficial || atual?.numeroOficial || null,
        linkPath,
        codigo: data.editalCodigo || atual?.codigo || null,
      });
    } catch (e) {
      setErro(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [processoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const diasRestantes = useMemo(() => diasAte(form.dataEncerramento), [form.dataEncerramento]);
  const alertaPrazo =
    diasRestantes != null && diasRestantes >= 0 && diasRestantes <= alertaDias;

  const statusUpper = String(form.status || '').toUpperCase();
  const prazoEsgotado = diasRestantes != null && diasRestantes < 0;
  const podeReabrir =
    !disabled &&
    form.numero < limite &&
    (['ENCERRADO', 'SEM_VENCEDOR', 'CANCELADO'].includes(statusUpper) || prazoEsgotado);

  const linkPrestadorAbsoluto = useMemo(() => {
    if (!portal.linkPath) return '';
    if (typeof window === 'undefined') return portal.linkPath;
    return `${window.location.origin}${portal.linkPath}`;
  }, [portal.linkPath]);

  const copiarLinkPrestador = async () => {
    if (!linkPrestadorAbsoluto) return;
    try {
      await navigator.clipboard.writeText(linkPrestadorAbsoluto);
      setMsg('Link do prestador copiado.');
    } catch {
      setErro('Não foi possível copiar. Selecione o link manualmente.');
    }
  };

  const atualizarPrazoDias = (prazo) => {
    const n = Math.max(1, Number(prazo) || 1);
    const abertura = form.dataAbertura ? new Date(form.dataAbertura) : new Date();
    const enc = new Date(abertura.getTime() + n * 24 * 60 * 60 * 1000);
    setForm((prev) => ({
      ...prev,
      prazoDias: n,
      dataEncerramento: toLocalInput(enc),
    }));
  };

  const salvar = async ({ avancar = false } = {}) => {
    if (!processoId) return;
    if (!form.dataAbertura || !form.dataEncerramento) {
      setErro('Informe abertura e encerramento.');
      return;
    }
    if (new Date(form.dataEncerramento) <= new Date(form.dataAbertura)) {
      setErro('Encerramento deve ser posterior à abertura.');
      return;
    }
    setSaving(true);
    setErro('');
    setMsg('');
    try {
      const editalAtual = {
        numero: form.numero,
        dataAbertura: form.dataAbertura,
        dataEncerramento: form.dataEncerramento,
        prazoDias: form.prazoDias,
        textoEdital: form.textoEdital || '',
        status: form.status === 'RASCUNHO' ? 'ABERTO' : form.status || 'ABERTO',
        modoPublico: !!form.modoPublico,
      };
      const res = await apiFetch(`/api/contratos/processos/${processoId}/extensoes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          editalAtual,
          dataAssinaturaPrevista: form.dataAssinaturaPrevista || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar edital');
      setMsg('Edital salvo no servidor.');
      onSaved?.(data.processo || null);
      if (avancar && onConfirm) {
        await onConfirm({
          dataAbertura: form.dataAbertura,
          dataEncerramento: form.dataEncerramento,
          modoPublico: !!form.modoPublico,
          textoEdital: form.textoEdital,
          numero: form.numero,
          prazoDias: form.prazoDias,
          dataAssinaturaPrevista: form.dataAssinaturaPrevista,
        });
      }
      await carregar();
    } catch (e) {
      setErro(e.message || 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const reabrir = async () => {
    if (!processoId || !podeReabrir) return;
    setSaving(true);
    setErro('');
    setMsg('');
    try {
      const res = await apiFetch(`/api/contratos/processos/${processoId}/edital/reabrir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prazoDias: form.prazoDias || 7,
          modoPublico: !!form.modoPublico,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Não foi possível reabrir o edital');
      setMsg(`Edital nº ${data.processo?.editalAtual?.numero || ''} aberto.`);
      onSaved?.(data.processo || null);
      await carregar();
    } catch (e) {
      setErro(e.message || 'Falha ao reabrir');
    } finally {
      setSaving(false);
    }
  };

  const publicarNoPortal = async () => {
    if (!processoId) return;
    if (!form.dataAbertura || !form.dataEncerramento) {
      setErro('Informe abertura e encerramento antes de publicar.');
      return;
    }
    if (new Date(form.dataEncerramento) <= new Date(form.dataAbertura)) {
      setErro('Encerramento deve ser posterior à abertura.');
      return;
    }
    setSaving(true);
    setErro('');
    setMsg('');
    try {
      // Persiste rascunho e publica (número oficial + token) no mesmo fluxo
      const editalAtual = {
        numero: form.numero,
        dataAbertura: form.dataAbertura,
        dataEncerramento: form.dataEncerramento,
        prazoDias: form.prazoDias,
        textoEdital: form.textoEdital || '',
        status: 'ABERTO',
        modoPublico: !!form.modoPublico,
      };
      await apiFetch(`/api/contratos/processos/${processoId}/extensoes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          editalAtual,
          dataAssinaturaPrevista: form.dataAssinaturaPrevista || null,
        }),
      });

      const res = await apiFetch(`/api/contratos/processos/${processoId}/edital/publicar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dataAbertura: form.dataAbertura,
          dataEncerramento: form.dataEncerramento,
          prazoDias: form.prazoDias,
          textoEdital: form.textoEdital || '',
          modoPublico: !!form.modoPublico,
          dataAssinaturaPrevista: form.dataAssinaturaPrevista || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao publicar no portal');

      const num = data.numeroOficial || data.editalPublicado?.numeroOficial;
      setMsg(
        num
          ? `Edital n° ${num} publicado. Use “Copiar link do prestador”.`
          : 'Edital publicado no portal.'
      );
      onSaved?.(data.processo || null);
      await carregar();
    } catch (e) {
      setErro(e.message || 'Falha ao publicar');
    } finally {
      setSaving(false);
    }
  };

  if (!processoId) return null;
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {portal.numeroOficial ? (
          <Chip
            label={`Edital n° ${portal.numeroOficial}`}
            color="success"
            sx={{ fontWeight: 700 }}
          />
        ) : (
          <Chip
            label={`${form.numero}º edital (rascunho)`}
            color="primary"
            sx={{ fontWeight: 700 }}
          />
        )}
        <Chip label={form.status || 'RASCUNHO'} size="small" variant="outlined" />
        <Chip label={`Limite: ${limite} reabertura(s)`} size="small" variant="outlined" />
        {alertaPrazo && (
          <Chip label={`Encerra em ${diasRestantes} dia(s)`} color="warning" size="small" />
        )}
      </Stack>

      {portal.publicado && linkPrestadorAbsoluto ? (
        <Alert
          severity="success"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={copiarLinkPrestador}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Copiar link do prestador
            </Button>
          }
        >
          Publicado no portal. Número oficial:{' '}
          <strong>{portal.numeroOficial ? `n° ${portal.numeroOficial}` : portal.codigo || '—'}</strong>
          <Typography variant="caption" display="block" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
            {linkPrestadorAbsoluto}
          </Typography>
        </Alert>
      ) : (
        <Alert severity="info">
          O número oficial (ex.: Edital n° 001/2026) e o link do prestador são gerados apenas na{' '}
          <strong>publicação</strong> no portal — rascunho não consome numeração.
        </Alert>
      )}

      {alertaPrazo && (
        <Alert severity="warning">
          Edital próximo do encerramento (≤ {alertaDias} dias). Avalie prorrogação se ainda não houver
          proposta vencedora.
        </Alert>
      )}

      {objetoTr ? (
        <Alert severity="info">Texto pré-preenchido a partir do objeto do TR (editável).</Alert>
      ) : null}

      {erro && (
        <Alert severity="error" onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}
      {msg && (
        <Alert severity="success" onClose={() => setMsg('')}>
          {msg}
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Texto do edital
        </Typography>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 1,
            border: '1px solid rgba(15,23,42,0.12)',
            '& .ql-container': { minHeight: 140 },
          }}
        >
          <ReactQuill
            theme="snow"
            value={form.textoEdital || ''}
            onChange={(v) => setForm((p) => ({ ...p, textoEdital: v }))}
            modules={QUILL_EDITAL_MODULES}
            readOnly={disabled}
          />
        </Box>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Abertura"
          type="datetime-local"
          value={form.dataAbertura || ''}
          onChange={(e) => setForm((p) => ({ ...p, dataAbertura: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
          disabled={disabled}
        />
        <TextField
          label="Encerramento"
          type="datetime-local"
          value={form.dataEncerramento || ''}
          onChange={(e) => setForm((p) => ({ ...p, dataEncerramento: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
          disabled={disabled}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Prazo de vigência (dias)"
          type="number"
          value={form.prazoDias ?? 7}
          onChange={(e) => atualizarPrazoDias(e.target.value)}
          fullWidth
          disabled={disabled}
          inputProps={{ min: 1 }}
          helperText="Recalcula a data de encerramento a partir da abertura"
        />
        <TextField
          label="Data assinatura prevista"
          type="date"
          value={(form.dataAssinaturaPrevista || '').slice(0, 10)}
          onChange={(e) => setForm((p) => ({ ...p, dataAssinaturaPrevista: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={disabled}
        />
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={!!form.modoPublico}
            onChange={(e) => setForm((p) => ({ ...p, modoPublico: e.target.checked }))}
            disabled={disabled}
            color="primary"
          />
        }
        label={
          form.modoPublico
            ? 'Público (visível na lista de editais abertos)'
            : 'Convidados (acesso apenas por código)'
        }
      />

      {historico.length > 0 && (
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.1)' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>Histórico de editais ({historico.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              {historico.map((h, idx) => (
                <Box
                  key={`${h.numero || idx}-${h.dataAbertura || idx}`}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(15,23,42,0.03)',
                    border: '1px solid rgba(15,23,42,0.08)',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    {h.numeroOficial
                      ? `Edital n° ${h.numeroOficial}`
                      : `${h.numero || idx + 1}º edital`}{' '}
                    — {h.status || 'ENCERRADO'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {h.dataAbertura || '—'} → {h.dataEncerramento || '—'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {showActions && (
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          {onCancel && (
            <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
          )}
          {portal.publicado && linkPrestadorAbsoluto && (
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={copiarLinkPrestador}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              Copiar link do prestador
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={reabrir}
            disabled={saving || !podeReabrir}
            sx={{ textTransform: 'none' }}
          >
            Abrir novo edital
          </Button>
          <Button
            variant="outlined"
            onClick={() => salvar({ avancar: false })}
            disabled={saving || disabled}
            sx={{ textTransform: 'none' }}
          >
            {saving ? '...' : confirmLabel}
          </Button>
          {!portal.publicado ? (
            <Button
              variant="contained"
              color="success"
              onClick={publicarNoPortal}
              disabled={saving || disabled}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              Publicar no portal e gerar link
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={publicarNoPortal}
              disabled={saving || disabled}
              sx={{ textTransform: 'none' }}
            >
              Atualizar publicação
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export { formEditalPadrao, diasAte };
